import React, { useMemo } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { Button } from "../ui/button";
import { Checkbox } from "../ui/checkbox";
import { Users, Plus, X } from "lucide-react";
import DateDropdownPicker from "../ui/DateDropdownPicker";
import { debtCreditTypes, currencies, romanianBanks } from "../../utils/mockData";

const MONTHS_RO = [
  { value: "01", label: "Ianuarie" }, { value: "02", label: "Februarie" },
  { value: "03", label: "Martie" }, { value: "04", label: "Aprilie" },
  { value: "05", label: "Mai" }, { value: "06", label: "Iunie" },
  { value: "07", label: "Iulie" }, { value: "08", label: "August" },
  { value: "09", label: "Septembrie" }, { value: "10", label: "Octombrie" },
  { value: "11", label: "Noiembrie" }, { value: "12", label: "Decembrie" },
];

const MonthYearPicker = ({ value, onChange, id }) => {
  const parsed = useMemo(() => {
    if (!value) return { month: "", year: "" };
    const parts = value.split("-");
    if (parts.length >= 2) return { year: parts[0], month: parts[1] };
    return { month: "", year: "" };
  }, [value]);

  const currentYear = new Date().getFullYear();
  const years = useMemo(() => {
    const arr = [];
    for (let y = currentYear + 30; y >= 2000; y--) arr.push(String(y));
    return arr;
  }, [currentYear]);

  const emitChange = (field, val) => {
    const next = { ...parsed, [field]: val };
    if (next.year && next.month) {
      onChange(`${next.year}-${next.month}`);
    } else {
      onChange(`${next.year || ""}-${next.month || ""}`);
    }
  };

  return (
    <div className="flex gap-2" data-testid={id}>
      <Select value={parsed.month} onValueChange={(v) => emitChange("month", v)}>
        <SelectTrigger className="flex-1 min-w-[110px]">
          <SelectValue placeholder="Luna" />
        </SelectTrigger>
        <SelectContent className="max-h-[200px]">
          {MONTHS_RO.map(m => <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>)}
        </SelectContent>
      </Select>
      <Select value={parsed.year} onValueChange={(v) => emitChange("year", v)}>
        <SelectTrigger className="w-[85px]">
          <SelectValue placeholder="An" />
        </SelectTrigger>
        <SelectContent className="max-h-[200px]">
          {years.map(y => <SelectItem key={y} value={y}>{y}</SelectItem>)}
        </SelectContent>
      </Select>
    </div>
  );
};

const DebtSection = ({ data, onChange }) => {
  const debts = Array.isArray(data) ? data : [];

  const handleDebtChange = (index, field, value) => {
    const newDebts = [...debts];
    newDebts[index] = { ...newDebts[index], [field]: value };
    onChange(newDebts);
  };

  const addDebt = () => {
    onChange([...debts, {
      id: debts.length + 1,
      bankName: "",
      creditType: "Ipotecar",
      grantDate: "",
      endDate: "",
      remainingBalance: "",
      monthlyRate: "",
      currency: "LEI"
    }]);
  };

  const removeDebt = (index) => {
    onChange(debts.filter((_, i) => i !== index));
  };

  return (
    <Card className="mb-6">
      <CardHeader className="bg-gradient-to-r from-blue-50 to-green-50 border-b">
        <CardTitle className="flex items-center gap-2 text-blue-900">
          <Users className="h-5 w-5" />
          Datorii Existente
        </CardTitle>
      </CardHeader>
      <CardContent className="p-6">
        <div className="space-y-6">
          <div className="flex items-center justify-between">
            <p className="text-sm text-gray-600">Adăugați toate creditele și împrumuturile existente</p>
            <Button type="button" onClick={addDebt} variant="outline" size="sm" className="flex items-center gap-2">
              <Plus className="h-4 w-4" /> Adaugă Credit/Datorie
            </Button>
          </div>

          {debts.map((debt, index) => (
            <div key={debt.id} className="p-6 border border-gray-200 rounded-lg bg-gray-50">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-gray-900">Credit/Datorie #{index + 1}</h3>
                {debts.length > 1 && (
                  <Button type="button" onClick={() => removeDebt(index)} variant="destructive" size="sm">
                    <X className="h-4 w-4" />
                  </Button>
                )}
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <Label>Nume Bancă</Label>
                  <Select value={debt.bankName} onValueChange={(v) => handleDebtChange(index, 'bankName', v)}>
                    <SelectTrigger><SelectValue placeholder="Selectați banca" /></SelectTrigger>
                    <SelectContent>
                      {romanianBanks.map(bank => <SelectItem key={bank} value={bank}>{bank}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label>Tip Credit</Label>
                  <Select value={debt.creditType} onValueChange={(v) => handleDebtChange(index, 'creditType', v)}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {debtCreditTypes.map(type => <SelectItem key={type} value={type}>{type}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>

                {/* Data Acordare - Dropdown Zi/Luna/An */}
                <div className="space-y-2">
                  <Label>Data Acordare</Label>
                  <DateDropdownPicker
                    id={`debt-grantDate-${index}`}
                    value={debt.grantDate}
                    onChange={(val) => handleDebtChange(index, 'grantDate', val)}
                  />
                </div>

                {/* Data Finalizare Credit - Luna + An */}
                <div className="space-y-2">
                  <Label>Data Finalizare Credit</Label>
                  <MonthYearPicker
                    id={`debt-endDate-${index}`}
                    value={debt.endDate || ""}
                    onChange={(val) => handleDebtChange(index, 'endDate', val)}
                  />
                </div>

                <div className="space-y-2">
                  <Label>Sold Rămas</Label>
                  <div className="flex gap-2">
                    <Input
                      type="number"
                      value={debt.remainingBalance}
                      onChange={(e) => handleDebtChange(index, 'remainingBalance', e.target.value)}
                      placeholder="Soldul rămas"
                      className="flex-1"
                    />
                    <Select value={debt.currency} onValueChange={(v) => handleDebtChange(index, 'currency', v)}>
                      <SelectTrigger className="w-20"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {currencies.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label>Rata Lunară</Label>
                  <div className="flex gap-2">
                    <Input
                      type="number"
                      value={debt.monthlyRate}
                      onChange={(e) => handleDebtChange(index, 'monthlyRate', e.target.value)}
                      placeholder="Rata lunară"
                      className="flex-1"
                    />
                    <Select value={debt.rateCurrency || debt.currency || "LEI"} onValueChange={(v) => handleDebtChange(index, 'rateCurrency', v)}>
                      <SelectTrigger className="w-20"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {currencies.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="flex items-center space-x-2 md:col-span-2">
                  <Checkbox
                    id={`debt-refinance-${index}`}
                    checked={debt.willRefinance || false}
                    onCheckedChange={(checked) => handleDebtChange(index, 'willRefinance', checked)}
                  />
                  <Label htmlFor={`debt-refinance-${index}`}>Se refinanțează prin acest credit?</Label>
                </div>
              </div>
            </div>
          ))}

          {debts.length === 0 && (
            <div className="text-center py-8 text-gray-500">
              <Users className="h-12 w-12 mx-auto mb-4 text-gray-300" />
              <p>Nu aveți credite sau datorii existente adăugate.</p>
              <p className="text-sm">Apăsați butonul "Adaugă Credit/Datorie" pentru a începe.</p>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
};

export default DebtSection;
